/**
 * PDA 扫描结果反馈相关类型，与后端 GraphQL `ScanResult<T>` / `PromptInfo` / `PopupType` 对齐。
 *
 * 共 4 个 PDA 扫描入口复用：Arrival / Sorting / Bagging / Bag Merge。
 *
 * 仅供 `pages/pda/scanResult/` 内部 + 4 个扫描页消费，不放全局组件目录。
 */

/** 弹窗级别整数，对应后端 `PopupType` int。 */
export const PopupType = {
    SUCCESS: 0,
    INTERCEPT: 1,
    NEED_ACTION: 2,
    NOTICE: 3,
    /** 成功并提示：扫描业务成功但需展示补充信息（如错分提示），使用橙色样式 */
    SUCCESS_WITH_NOTICE: 4,
    INTERCEPT_NO_POP: 5,
    NEED_ACTION_NO_POP: 6,
    /** 成功并展示内容：绿色边框 + 绿色弹窗，多流向装包成功时展示流向和尾程商信息 */
    SUCCESS_WITH_BORDER: 7,
} as const;
export type PopupType = typeof PopupType[keyof typeof PopupType];

/** 后端 `PromptInfo`：messageKey 用于埋点，popupType 决定视觉，title/content 已按 language 渲染。 */
export interface ScanPromptInfo {
    messageKey: string;
    popupType: PopupType;
    /**
     * mutation 级固定 title 文案（如「入库失败」/「分拣失败」），后端按 app_language 渲染；
     * 后端 PromptInfo 永远会带（即使空串），但前端自构造的 fallback 场景（如本地 NOTICE）可省略。
     */
    title?: string;
    content: string;
}

/**
 * 后端 `ScanResult<T>` 通用外壳。
 *
 * - success: 业务是否成功（决定列表归属：true → 成功列，false → 失败列）
 * - message: 系统级兜底文案（仅在 systemError / 网络错误等场景出现，业务文案不走这里）
 * - data: 业务数据（Arrival 暂不消费 data；Bagging 等可承载 matchedBagNumber 等）
 * - promptInfo: 可空。null 时调用方按 success 自行决定要不要展示绿框 / 兜底提示
 */
export interface ScanResultEnvelope<T = unknown> {
    success: boolean;
    message?: string | null;
    data?: T | null;
    promptInfo?: ScanPromptInfo | null;
}
